import { useEffect } from "react";

export const SCROLL_RESTORE_KEY = "qq-scroll-y";
const PENDING_CLASS = "qq-scroll-pending";

export function isHomePath() {
  const path = window.location.pathname;
  return path === "/" || path === "";
}

export function isPageReload() {
  const nav = performance.getEntriesByType?.("navigation")?.[0];
  return nav?.type === "reload";
}

export function readSavedScrollY() {
  try {
    const raw = sessionStorage.getItem(SCROLL_RESTORE_KEY);
    if (raw === null) return null;
    const y = Number.parseInt(raw, 10);
    return Number.isFinite(y) && y >= 0 ? y : null;
  } catch {
    return null;
  }
}

function lockScroll() {
  document.documentElement.classList.add(PENDING_CLASS);
}

export function unlockScroll() {
  document.documentElement.classList.remove(PENDING_CLASS);
}

function clampScrollY(y) {
  const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  return Math.min(Math.max(0, y), maxY);
}

function persistScrollY() {
  try {
    sessionStorage.setItem(SCROLL_RESTORE_KEY, String(window.scrollY));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Keeps scroll position on browser refresh instead of flashing to the footer.
 */
export default function ScrollRestore() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const shouldRestore = isHomePath() && isPageReload();
    const savedY = readSavedScrollY();
    const targetY = shouldRestore ? (savedY ?? 0) : null;

    if (targetY === null) {
      unlockScroll();
      return undefined;
    }

    lockScroll();

    let attempts = 0;
    let lastHeight = 0;
    let stableTicks = 0;
    let cancelled = false;

    const tryRestore = () => {
      if (cancelled) return;

      const docHeight = document.documentElement.scrollHeight;
      const y = clampScrollY(targetY);

      window.scrollTo(0, y);

      if (docHeight === lastHeight) {
        stableTicks += 1;
      } else {
        stableTicks = 0;
        lastHeight = docHeight;
      }

      attempts += 1;

      const layoutStillGrowing = y < targetY && docHeight < targetY + window.innerHeight;
      const needsMorePasses = stableTicks < 4 || layoutStillGrowing;

      if (attempts < 40 && needsMorePasses) {
        requestAnimationFrame(tryRestore);
        return;
      }

      window.scrollTo(0, clampScrollY(targetY));
      unlockScroll();
    };

    requestAnimationFrame(tryRestore);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let persistRaf = 0;

    const schedulePersist = () => {
      window.cancelAnimationFrame(persistRaf);
      persistRaf = window.requestAnimationFrame(persistScrollY);
    };

    schedulePersist();
    window.addEventListener("scroll", schedulePersist, { passive: true });
    window.addEventListener("pagehide", persistScrollY);
    window.addEventListener("beforeunload", persistScrollY);

    return () => {
      window.cancelAnimationFrame(persistRaf);
      window.removeEventListener("scroll", schedulePersist);
      window.removeEventListener("pagehide", persistScrollY);
      window.removeEventListener("beforeunload", persistScrollY);
    };
  }, []);

  return null;
}
